import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Button,
	Typography,
	Dialog,
	DialogContent,
	DialogTitle,
	Grid,
	CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AMFIItem from './AMFIItem';
import AMFIDrawer from './AMFIDrawer';
import useDealStore from '../../stores/dealStore';

/**
 * AMFIEditor component for managing attributes
 * Handles creating, editing, and deleting attributes
 */
const AMFIEditor = ({
	tenantId,
	dealId,
	type = 'attribute', // Currently focused on attributes
	categories = [],
	items = [],
	onCreateRefReady = null, // Callback to pass handleOpenCreate to parent
	hideTitle = false,
}) => {
	// Get all relevant store methods
	const {
		// Attribute methods
		addAttribute,
		updateAttribute,
		deleteAttribute,
		// Metric methods
		addMetric,
		updateMetric,
		deleteMetric,
		// Fact methods
		addFact,
		updateFact,
		deleteFact,
		// Insight methods
		addInsight,
		updateInsight,
		deleteInsight,
		// Sorting function
		sortByIsKey,
		loading,
	} = useDealStore();

	// Local state
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editItem, setEditItem] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Confirmation dialog states
	const [reclassifyConfirmOpen, setReclassifyConfirmOpen] = useState(false);
	const [reclassifyData, setReclassifyData] = useState(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [deleteItemId, setDeleteItemId] = useState(null);

	// Handler for opening the create form
	const handleOpenCreate = () => {
		setEditItem(null);
		setDialogOpen(true);
	};

	// Pass handleOpenCreate to parent component if callback provided
	useEffect(() => {
		if (onCreateRefReady) {
			onCreateRefReady(handleOpenCreate);
		}
	}, [onCreateRefReady]);

	// Handler for opening the edit form
	const handleEdit = (item) => {
		setEditItem(item);
		setDialogOpen(true);
	};

	// Handler for closing the dialog
	const handleCloseDialog = () => {
		setDialogOpen(false);
		setEditItem(null);
	};

	// Get the appropriate store methods based on type
	const getStoreMethods = () => {
		switch (type) {
			case 'metric':
				return {
					add: addMetric,
					update: updateMetric,
					delete: deleteMetric,
					loadingState: loading.metrics,
				};
			case 'fact':
				return {
					add: addFact,
					update: updateFact,
					delete: deleteFact,
					loadingState: loading.facts,
				};
			case 'insight':
				return {
					add: addInsight,
					update: updateInsight,
					delete: deleteInsight,
					loadingState: loading.insights,
				};
			case 'attribute':
			default:
				return {
					add: addAttribute,
					update: updateAttribute,
					delete: deleteAttribute,
					loadingState: loading.attributes,
				};
		}
	};

	const { add, update, delete: deleteItem, loadingState } = getStoreMethods();

	// Handler for form submission (create or edit)
	const handleSubmit = async (data, mode) => {
		setIsSubmitting(true);
		try {
			if (mode === 'create') {
				await add(tenantId, dealId, data);
			} else {
				await update(tenantId, dealId, { ...data, id: editItem.id });
			}
			handleCloseDialog();
		} catch (error) {
			console.error(
				`Error ${mode === 'create' ? 'creating' : 'updating'} ${type}:`,
				error
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Open delete confirmation dialog
	const openDeleteConfirm = (id) => {
		setDeleteItemId(id);
		setDeleteConfirmOpen(true);
	};

	// Close delete confirmation dialog
	const closeDeleteConfirm = () => {
		setDeleteConfirmOpen(false);
		setDeleteItemId(null);
	};

	// Handler for deleting an item
	const handleDelete = (id) => {
		// Open confirmation dialog instead of using window.confirm
		openDeleteConfirm(id);
	};

	// Execute the actual deletion after confirmation
	const executeDelete = async () => {
		if (!deleteItemId) return;

		setIsSubmitting(true);
		try {
			await deleteItem(tenantId, dealId, deleteItemId);
			closeDeleteConfirm();
		} catch (error) {
			console.error(`Error deleting ${type}:`, error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Open reclassify confirmation dialog
	const openReclassifyConfirm = (item, newType) => {
		setReclassifyData({ item, newType });
		setReclassifyConfirmOpen(true);
	};

	// Close reclassify confirmation dialog
	const closeReclassifyConfirm = () => {
		setReclassifyConfirmOpen(false);
		setReclassifyData(null);
	};

	// Handler for reclassifying an item to a different AMFI type
	const handleReclassify = async (item, newType) => {
		// Open confirmation dialog instead of using window.confirm
		openReclassifyConfirm(item, newType);
	};

	// Handler for toggling key status of an item
	const handleToggleKey = async (item) => {
		const { update } = getStoreMethods();

		try {
			setIsSubmitting(true);
			// Update the item with toggled is_key value
			await update(tenantId, dealId, {
				...item,
				is_key: !item.is_key,
			});
		} catch (error) {
			console.error(`Error toggling key status for ${type}:`, error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Execute the actual reclassification after confirmation
	const executeReclassify = async () => {
		if (!reclassifyData) return;

		const { item, newType } = reclassifyData;
		setIsSubmitting(true);

		try {
			// Get the store methods for the new type
			const newTypeMethods = (() => {
				switch (newType) {
					case 'metric':
						return { add: addMetric };
					case 'fact':
						return { add: addFact };
					case 'insight':
						return { add: addInsight };
					case 'attribute':
						return { add: addAttribute };
					default:
						return { add: null };
				}
			})();

			if (!newTypeMethods.add) {
				console.error('Invalid target type for reclassification');
				return;
			}

			// Prepare common data to preserve
			const commonData = {
				category: item.category,
				source: item.source,
				is_key: item.is_key || false,
			};

			// Prepare data specific to the new type
			let newData = { ...commonData };

			if (newType === 'metric') {
				// Convert to metric
				newData.metric_name = item.name || item.metric_name || '';
				newData.value = item.value || '';
				newData.unit = item.unit || '';
			} else if (newType === 'attribute') {
				// Convert to attribute
				newData.name = item.name || item.metric_name || '';
				newData.value = item.value || item.content || '';
			} else if (newType === 'fact' || newType === 'insight') {
				// Convert to fact or insight
				newData.content = item.content || item.value || '';
			}

			// First add to the new type
			await newTypeMethods.add(tenantId, dealId, newData);

			// Then delete from the old type
			await deleteItem(tenantId, dealId, item.id);

			// Close the confirmation dialog
			closeReclassifyConfirm();
		} catch (error) {
			console.error(`Error reclassifying ${type} to ${newType}:`, error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Determine if we should use grid layout based on type
	const useGridLayout = type === 'attribute' || type === 'metric';

	return (
		<Box>
			{!hideTitle && (
				<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
					<Typography variant='h6' component='h2'>
						{`${type.charAt(0).toUpperCase() + type.slice(1)}s`}
					</Typography>
					<Button
						variant='outlined'
						size='small'
						startIcon={<AddIcon />}
						onClick={handleOpenCreate}
						sx={{ ml: 2 }}
					>
						Add
					</Button>
				</Box>
			)}

			{/* Conditional layout based on AMFI type */}
			{useGridLayout ? (
				// Grid layout for Attributes and Metrics with responsive breakpoints
				<Grid container spacing={2}>
					{sortByIsKey(items).map((item) => (
						<Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.id}>
							<AMFIItem
								item={item}
								type={type}
								onEdit={handleEdit}
								onDelete={handleDelete}
								onReclassify={handleReclassify}
								onToggleKey={handleToggleKey}
							/>
						</Grid>
					))}
				</Grid>
			) : (
				// Single column layout for Facts and Insights
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					{sortByIsKey(items).map((item) => (
						<Box key={item.id} sx={{ width: '100%' }}>
							<AMFIItem
								item={item}
								type={type}
								onEdit={handleEdit}
								onDelete={handleDelete}
								onReclassify={handleReclassify}
								onToggleKey={handleToggleKey}
							/>
						</Box>
					))}
				</Box>
			)}

			{/* Loading indicator */}
			{loadingState && (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
					<CircularProgress size={24} />
				</Box>
			)}

			{/* Empty state */}
			{!loadingState && items.length === 0 && (
				<Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
					No {type}s available. Click &quot;Add{' '}
					{type.charAt(0).toUpperCase() + type.slice(1)}&quot; to create one.
				</Typography>
			)}

			{/* Drawer for create/edit form */}
			<AMFIDrawer
				open={dialogOpen}
				onClose={handleCloseDialog}
				type={type}
				item={editItem}
				categories={categories}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
			/>

			{/* Delete confirmation dialog */}
			<Dialog
				open={deleteConfirmOpen}
				onClose={closeDeleteConfirm}
				aria-labelledby='delete-dialog-title'
			>
				<DialogTitle id='delete-dialog-title'>
					Delete {type.charAt(0).toUpperCase() + type.slice(1)}
				</DialogTitle>
				<DialogContent>
					<Typography variant='body1'>
						Are you sure you want to delete this {type}? This action cannot be
						undone.
					</Typography>
				</DialogContent>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
					<Button
						onClick={closeDeleteConfirm}
						color='primary'
						sx={{ mr: 1 }}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					<Button
						onClick={executeDelete}
						variant='contained'
						color='error'
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<CircularProgress size={24} color='inherit' />
						) : (
							'Delete'
						)}
					</Button>
				</Box>
			</Dialog>

			{/* Reclassify confirmation dialog */}
			<Dialog
				open={reclassifyConfirmOpen}
				onClose={closeReclassifyConfirm}
				aria-labelledby='reclassify-dialog-title'
			>
				<DialogTitle id='reclassify-dialog-title'>Change Item Type</DialogTitle>
				<DialogContent>
					<Typography variant='body1'>
						Are you sure you want to change this {type} to a{' '}
						{reclassifyData?.newType}?
						<br />
						<br />
						This action will convert the item while preserving as much data as
						possible.
					</Typography>
				</DialogContent>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
					<Button
						onClick={closeReclassifyConfirm}
						color='primary'
						sx={{ mr: 1 }}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					<Button
						onClick={executeReclassify}
						variant='contained'
						color='primary'
						disabled={isSubmitting}
					>
						{isSubmitting ? (
							<CircularProgress size={24} color='inherit' />
						) : (
							'Change Type'
						)}
					</Button>
				</Box>
			</Dialog>
		</Box>
	);
};

AMFIEditor.propTypes = {
	tenantId: PropTypes.string.isRequired,
	dealId: PropTypes.string.isRequired,
	type: PropTypes.oneOf(['attribute', 'metric', 'fact', 'insight']),
	categories: PropTypes.array,
	items: PropTypes.array,
	onCreateRefReady: PropTypes.func,
	hideTitle: PropTypes.bool,
};

export default AMFIEditor;
